/**
 * Where a household writes what it thinks of Two Plates (#1336).
 *
 * ⛔ A HOUSEHOLD MUST HAVE COOKED TO WRITE ONE. The server enforces it; this
 * screen just explains it, because "why can't I review this" is otherwise a
 * support email. Signing up is not an opinion, and a wall of reviews by people
 * who opened the app once is the same lie as a fabricated one, told slower.
 *
 * ⚠️ Nothing written here appears anywhere until somebody reads it. Editing a
 * published review returns it to pending — a published review that can be
 * silently rewritten afterwards is an unmoderated review with extra steps, and
 * the screen says so rather than letting it be a surprise.
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { twoPlates, type MyReview } from "../api/twoPlates";

const STATUS_COPY: Record<string, string> = {
  pending: "Waiting to be read. Nothing appears on the site until it is.",
  published: "Live on twoplates.me. Thank you — this is the thing that helps people decide.",
  hidden: "Not shown on the site.",
};

export default function Review() {
  const queryClient = useQueryClient();
  const q = useQuery<MyReview>({ queryKey: ["my-review"], queryFn: twoPlates.myReview });

  const [displayName, setDisplayName] = useState("");
  const [rating, setRating] = useState(0);
  const [headline, setHeadline] = useState("");
  const [body, setBody] = useState("");
  const [saved, setSaved] = useState(false);

  // Fill the form from an existing review once, when it arrives.
  useEffect(() => {
    const r = q.data?.review;
    if (!r) return;
    setDisplayName(r.displayName);
    setRating(r.rating);
    setHeadline(r.headline ?? "");
    setBody(r.body);
  }, [q.data?.review]);

  const save = useMutation({
    mutationFn: () =>
      twoPlates.saveReview({
        displayName: displayName.trim(),
        rating,
        headline: headline.trim() || undefined,
        body: body.trim(),
      }),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["my-review"] });
    },
  });

  if (q.isLoading) return <p className="text-muted p-4 text-sm">Loading…</p>;

  if (q.data && !q.data.eligible) {
    return (
      <div className="p-4">
        <h1 className="text-lg font-semibold">Tell people how it went</h1>
        <p className="text-muted mt-2 max-w-prose text-sm">
          {q.data.reason ?? "Cook something first."}
        </p>
        <p className="text-muted mt-3 max-w-prose text-xs">
          We only publish reviews from households that have actually cooked from a
          plan. It makes the reviews worth reading.
        </p>
      </div>
    );
  }

  const valid = displayName.trim().length > 0 && rating >= 1 && body.trim().length >= 10;
  const existing = q.data?.review;

  return (
    <div className="p-4">
      <h1 className="text-lg font-semibold">Tell people how it went</h1>
      <p className="text-muted mt-1 max-w-prose text-sm">
        For somebody deciding whether this is worth it. Be honest — a wall of
        five-star raves helps nobody choose.
      </p>

      {existing && (
        <p className="border-line text-muted mt-3 rounded-lg border p-3 text-xs">
          {STATUS_COPY[existing.status] ?? existing.status}
        </p>
      )}

      <div className="mt-5 flex max-w-prose flex-col gap-4">
        <div>
          <span className="text-muted text-xs uppercase tracking-wide">Rating</span>
          <div className="mt-1.5 flex gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setRating(n)}
                aria-pressed={rating === n}
                aria-label={`${n} out of 5`}
                className="border-line flex-1 rounded-lg border py-2 text-sm font-medium"
                style={
                  rating >= n
                    ? { borderColor: "var(--color-plate-b)", color: "var(--color-plate-b)" }
                    : undefined
                }
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-muted text-xs uppercase tracking-wide">Name to show</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={40}
            placeholder="Dana R."
            className="border-line rounded-lg border px-3 py-2 text-sm"
          />
          {/* Said plainly. Somebody recommending a meal planner did not agree to
              publish who they are. */}
          <span className="text-muted text-[11px]">
            Shown publicly. Use whatever you like — we never show your email.
          </span>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-muted text-xs uppercase tracking-wide">Headline (optional)</span>
          <input
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            maxLength={80}
            placeholder="We stopped cooking twice"
            className="border-line rounded-lg border px-3 py-2 text-sm"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-muted text-xs uppercase tracking-wide">What happened</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={1500}
            rows={6}
            placeholder="Who's in your house, what you disagree about, and whether this actually fixed it."
            className="border-line rounded-lg border px-3 py-2 text-sm"
          />
          <span className="text-muted text-[11px]">{body.trim().length} characters, 10 minimum</span>
        </label>

        <div className="flex items-center gap-3">
          <button
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
            className="bg-plate-b text-ground rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-40"
          >
            {save.isPending ? "Saving…" : existing ? "Save changes" : "Submit"}
          </button>
          {saved && <span className="text-muted text-sm">Saved — waiting to be read.</span>}
        </div>

        {existing?.status === "published" && (
          <p className="text-muted text-[11px]">
            ⚠️ Saving changes takes it back off the site until it is read again.
          </p>
        )}

        {save.isError && (
          <p role="alert" className="text-warn text-sm">
            That did not save. Try again.
          </p>
        )}
      </div>
    </div>
  );
}
